import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOpenRouterBalance } from '../../hooks/useOpenRouterBalance';
import {
  ACCOUNT_READY_MESSAGE,
  BETA_ALLOWANCE_UNAVAILABLE_MESSAGE,
  BETA_ALLOWANCE_USED_MESSAGE,
  BETA_BLOCKED_MESSAGE,
  OWN_KEY_IN_USE_MESSAGE,
  allowanceLabel,
  betaWriterLabel,
  formatAllowanceAmount,
  isBetaWriter,
} from '../../utils/betaAllowance';
import { openSettingsAtApiKey } from '../../utils/openSettings';
import { useEngagementMessage } from '../../context/EngagementMessageContext';
import { FuelGaugeDetailModal } from './FuelGaugeDetailModal';
import { DrumCounterFace } from './DrumCounterFace';
import './FuelGauge.css';

// Existing engagement-messaging trigger (unrelated to the lamp below — this
// proactively nudges the writer with in-app messaging; kept at its original
// threshold and behavior).
const ENGAGEMENT_LOW_THRESHOLD = 1.0;

// Low-fuel lamp: lit below this balance. Distinct from the engagement
// threshold above — the lamp is a steady instrument-face indicator, not a
// one-time nudge.
const LAMP_THRESHOLD = 2.0;

const FLICKER_MS = 220;

export function FuelGauge({ onOpenSettings }) {
  const { data, loading, refresh, isRefreshCooldown } = useOpenRouterBalance();
  const { checkForMessages } = useEngagementMessage();
  const [showDetail, setShowDetail] = useState(false);
  const [flicker, setFlicker] = useState(false);
  const hasTriggeredLowBalance = useRef(false);

  // Trigger engagement messaging system when balance drops low.
  useEffect(() => {
    if (data && data.balance !== null && data.balance < ENGAGEMENT_LOW_THRESHOLD) {
      if (!hasTriggeredLowBalance.current) {
        hasTriggeredLowBalance.current = true;
        checkForMessages();
      }
    } else {
      hasTriggeredLowBalance.current = false;
    }
  }, [data, checkForMessages]);

  // Determine instrument state. A truthful finite dollar figure is only
  // available in the plain "has a real balance" case — no key or a fetch
  // error both lack a bounded number to roll into the drums, so they share
  // the same dash treatment rather than each inventing their own placeholder
  // digits. An own key whose credits cannot be read says so in words.
  // A used-up beta allowance is not a dollar figure.
  const isPending = data?.key_provisioning === 'pending';
  const isBlocked = data?.key_provisioning === 'blocked';
  const isOwnNeeded = data?.key_provisioning === 'own_needed';
  const isOwn = data?.key_provisioning === 'own';
  const betaWriter = isBetaWriter(data) && !isOwnNeeded && !isOwn;
  // A beta writer never falls through to the own-key drums, including when
  // the read failed or the key was reported invalid.
  const isNoKey = !betaWriter && !isOwn && (!data || data.error === 'No OpenRouter key configured');
  const isInvalid = !betaWriter && !!data?.error?.includes?.('Invalid');
  const isLoadingFirstFetch = loading && !data;
  const readableBalance = typeof data?.balance === 'number' && Number.isFinite(data.balance);

  const hasNumericBalance =
    !isLoadingFirstFetch &&
    !isNoKey &&
    !isInvalid &&
    !isPending &&
    !isBlocked &&
    !isOwnNeeded &&
    readableBalance;
  const isOwnNeutral = isOwn && !isInvalid && !isLoadingFirstFetch && !readableBalance;

  const isProvisionedBalance = hasNumericBalance && betaWriter;
  const isBetaError = betaWriter && !hasNumericBalance && !isPending && !isBlocked && Boolean(data?.error);
  // An app-provisioned account that does not yet have a figure (still being
  // set up, stopped, or waiting on the allowance) uses the same words as the
  // hover and the dialog — never the own-key BALANCE drums.
  const isBetaPlain = !isProvisionedBalance && (
    isPending
    || isBlocked
    || isBetaError
    || betaWriter
  );
  const betaPlainCopy = isBlocked
    ? betaWriterLabel(data?.allowance_label, BETA_BLOCKED_MESSAGE)
    : isBetaError
      ? BETA_ALLOWANCE_UNAVAILABLE_MESSAGE
      : betaWriterLabel(data?.allowance_label, ACCOUNT_READY_MESSAGE);

  let ariaLabel;
  if (isLoadingFirstFetch) {
    ariaLabel = 'Loading balance';
  } else if (isOwnNeeded) {
    ariaLabel = BETA_ALLOWANCE_USED_MESSAGE;
  } else if (isOwnNeutral) {
    ariaLabel = OWN_KEY_IN_USE_MESSAGE;
  } else if (isPending) {
    ariaLabel = betaWriterLabel(data?.allowance_label, ACCOUNT_READY_MESSAGE);
  } else if (isBlocked) {
    ariaLabel = betaWriterLabel(data?.allowance_label, BETA_BLOCKED_MESSAGE);
  } else if (isNoKey) {
    ariaLabel = 'No OpenRouter key configured';
  } else if (isInvalid) {
    ariaLabel = 'Invalid OpenRouter API key';
  } else if (isProvisionedBalance) {
    // Face, hover, and the detail dialog all read this same cents figure.
    // The balance hook refreshes it after a send (openrouter:balance-changed).
    ariaLabel = allowanceLabel(data.balance);
  } else if (isBetaPlain) {
    ariaLabel = betaPlainCopy;
  } else if (hasNumericBalance) {
    ariaLabel = `Balance: $${data.balance.toFixed(2)}`;
  } else {
    // A fetch error whose message doesn't match the specific no-key/invalid
    // strings above (e.g. useOpenRouterBalance's generic catch-block
    // fallback) — still an unavailable-balance state, not a crash.
    ariaLabel = 'Unable to load balance';
  }

  const lampLit = hasNumericBalance && data.balance < LAMP_THRESHOLD;

  const handleClick = () => {
    setShowDetail(true);
    refresh();
    setFlicker(true);
    setTimeout(() => setFlicker(false), FLICKER_MS);
  };

  const handleAddOwnKey = (event) => {
    event.stopPropagation();
    if (onOpenSettings) onOpenSettings('api-key');
    else openSettingsAtApiKey();
  };

  const detail = showDetail && createPortal(
    <FuelGaugeDetailModal
      data={data}
      onClose={() => setShowDetail(false)}
      onRefresh={refresh}
      isRefreshCooldown={isRefreshCooldown}
      onOpenSettings={onOpenSettings}
    />,
    document.body
  );

  if (isOwnNeeded) {
    return (
      <>
        <div className="fuel-gauge fuel-gauge--allowance fuel-gauge--own-needed">
          <button type="button" className="fuel-gauge__own-needed" onClick={handleClick}>
            <span className="fuel-gauge__allowance-status">{BETA_ALLOWANCE_USED_MESSAGE}</span>
          </button>
          <button type="button" className="fuel-gauge__add-key" onClick={handleAddOwnKey}>
            Add your own key
          </button>
        </div>
        {detail}
      </>
    );
  }

  return (
    <>
      <button
        className={`fuel-gauge${isLoadingFirstFetch ? ' fuel-gauge--loading' : ''}${isProvisionedBalance || isBetaPlain || isOwnNeutral ? ' fuel-gauge--allowance' : ''}${flicker ? ' fuel-gauge--flicker' : ''}`}
        onClick={isLoadingFirstFetch ? undefined : handleClick}
        disabled={isLoadingFirstFetch}
        aria-label={ariaLabel}
        title={isBetaPlain || isOwnNeutral || hasNumericBalance ? ariaLabel : undefined}
      >
        {isOwnNeutral ? (
          <span className="fuel-gauge__allowance" aria-hidden="true">
            <span className="fuel-gauge__allowance-status">{OWN_KEY_IN_USE_MESSAGE}</span>
          </span>
        ) : isProvisionedBalance ? (
          <span className="fuel-gauge__allowance" aria-hidden="true">
            <span className="fuel-gauge__allowance-name">Beta allowance</span>
            <span className="fuel-gauge__allowance-amount">{formatAllowanceAmount(data.balance)}</span>
          </span>
        ) : isBetaPlain ? (
          <span className="fuel-gauge__allowance" aria-hidden="true">
            <span className="fuel-gauge__allowance-name">Beta allowance</span>
            <span className="fuel-gauge__allowance-status">{betaPlainCopy}</span>
          </span>
        ) : (
          <>
            <span className="fuel-gauge__label-stack" aria-hidden="true">
              <span className="fuel-gauge__label-line">BALANCE</span>
            </span>
            <DrumCounterFace
              balance={hasNumericBalance ? data.balance : null}
              available={hasNumericBalance}
              lampLit={lampLit}
            />
          </>
        )}
      </button>
      {detail}
    </>
  );
}
